import ApplicationStatus from '@sunbit-dev/common/enums/cardApply/ApplicationStatus';
import { formatDate } from '@sunbit-dev/common/helpers/dates';
import React from 'react';
import styled, { css } from 'styled-components';
import emptyFunction from '../../../../helpers/emptyFunction';
import cardPicture from '../../../assets/card-apply/cardNewDesign.svg';
import Dialog from '../../../ui/dialog/Dialog';
import Input2 from '../../../ui/input/Input2';
import SLink from '../../../ui/styled/SLink';
import CommonSection from '../../components/commonSection/CommonSection';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import Title from '../../components/title/Title';
import { IdentificationErrors } from '../identification/Identification';
import Tooltip, { TooltipPosition } from '../../../ui/tooltip/Tooltip';
import CardApplyButton from '../../components/cardApplyButton/CardApplyButton';
import CardApplyLink from '../../components/cardApplyLink/CardApplyLink';
import { ButtonColor } from '../../../ui/button/Button';
import { PrivacyOptions } from '@sunbit-dev/common/enums/Datadog';

// ############################ Styles ###########################
// tslint:disable:variable-name
const SIdentification = styled.div`
  display: flex;
  flex-direction: column;
`;

const SCardImage = styled.img(
  ({ theme }) => css`
    width: 100%;
    max-width: 400px;
    @media only screen and ${theme.device.laptopMS} {
      max-width: 518px;
      transform: translate(30px, 0px);
    }
    @media only screen and ${theme.device.max.tablet} {
      margin-bottom: 15px;
    }
  `
);

const SText = styled.text(
  ({ theme }) => css`
    color: ${theme.colors.primary};
  `
);

const SLayoutPartition = styled.section(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  @media only screen and ${theme.device.laptopS} {
    flex-direction: row-reverse;
    & + & {
      margin-left: ${theme.spacing.large}px;
    }
  }
`
);

const SLayoutColumn = styled.section(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  flex 1;
  position: relative;
  @media only screen and ${theme.device.laptopS} {
    & + & {
      margin-left: 20px;
    }
  }
`
);

const SInputSection = styled.form(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.spacing.large}px;
  max-width: 400px;
`
);

const SInputWrap = styled.div(
  ({ theme }) => `
  position: relative;
  margin-bottom: ${theme.spacing.mediumLarger}px;
`
);

const SSSNTooltip = styled(Tooltip)(
  () => `
  position: absolute;
  z-index: 1;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`
);

const SMediumInput = styled(Input2)(
  () => `
`
);

const SSubmitButton = styled(CardApplyButton).attrs({ stretch: true })``;

const SError = styled.section<{}>(
  ({ theme }) => `
  color: ${theme.colors.error};
`
);

// tslint:enable:variable-name
// ############################ End Styles ###########################

interface IExistingApplicationProps {
  firstName: string;
  ssnLast4Digits: string;
  applicationStatus: ApplicationStatus;
  accountCreationDate: string;
  submit: (ssnLast4Digits: string) => void;
  completeMyApplicationAction: () => void;
  error?: IdentificationErrors;
  plasticAddressModalVisible: boolean;
}

// tslint:disable-next-line:variable-name
const ExistingApplication: React.FC<IExistingApplicationProps> = (props) => {
  const [ssnLast4Digits, setSsnLast4Digits] = React.useState<string>('');

  React.useEffect(() => {
    setSsnLast4Digits(props.ssnLast4Digits);
  }, []);

  const handleSsnLast4Digits = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSsnLast4Digits(value);
  }, []);

  const isSubmitEnabled = () => ssnLast4Digits.length === 4;

  const handleSubmit = () => {
    if (isSubmitEnabled()) {
      props.submit(ssnLast4Digits);
    }
  };

  let applicationStatusElement: React.ReactNode = null;
  switch (props.applicationStatus) {
    case ApplicationStatus.COMPLETED:
      applicationStatusElement = (
        <Dialog data-dd-privacy={PrivacyOptions.Allow} onAction={emptyFunction}>
          <SText>
            You have already applied for the Sunbit Card and your application has been approved on {formatDate(props.accountCreationDate)}. If you haven&apos;t received your card within 7 to 10
            business days or you have any questions, please{' '}
            <CardApplyLink href="https://sunbit.com/contact-us/" target="_blank">
              contact us.
            </CardApplyLink>
          </SText>
        </Dialog>
      );
      break;
    case ApplicationStatus.CREDIT_APPROVED:
      applicationStatusElement = (
        <Dialog data-dd-privacy={PrivacyOptions.Allow} onAction={props.completeMyApplicationAction} action1={{ text: 'Complete my Application', value: '', color: ButtonColor.primaryLightBlue }}>
          <SText>
            You have already applied for the Sunbit Card and your application has been preliminarily approved. To complete the application, you must provide your payment method details. If you have
            any questions, please{' '}
          </SText>
          <CardApplyLink href="https://sunbit.com/contact-us/" target="_blank">
            contact us.
          </CardApplyLink>
        </Dialog>
      );
      break;
    case ApplicationStatus.DECLINED:
      applicationStatusElement = (
        <Dialog onAction={emptyFunction}>
          <SText>
            You have already applied for the Sunbit Card but unfortunately, we were unable to approve your application. We&apos;ve sent you a detailed notice via email with additional details.
          </SText>
        </Dialog>
      );
      break;
    default:
      applicationStatusElement = null;
      break;
  }

  const errorText = props.error === IdentificationErrors.detailsDontMatch ? 'The information provided does not match our records. Please try again.' : '';

  return (
    <SIdentification data-dd-privacy={PrivacyOptions.MaskUserInput}>
      {!props.plasticAddressModalVisible && applicationStatusElement}
      <SLayoutPartition>
        <SCardImage src={cardPicture} alt="card picture" />
        <SLayoutColumn>
          <CommonSection>
            <Title data-at="sunbit-card-title">The Sunbit Card</Title>
          </CommonSection>
          <CommonSection>
            <SectionTitle data-at="spec-introduction" spacing={20}>
              <span data-dd-privacy={PrivacyOptions.Mask}>{props.firstName}</span>
              ,
              <br />
              We see that you&apos;ve already started your application. Please enter the last 4 digits of your SSN to return to your application.
            </SectionTitle>
          </CommonSection>
          <CommonSection>
            <SError data-at="error-message">{errorText}</SError>
          </CommonSection>
          <SInputSection>
            <SInputWrap>
              <SMediumInput
                color={'primary'}
                placeholder="Last 4 digits of your SSN"
                name="ssnLast4Digits"
                dataAt="last4DigitsSSN"
                value={ssnLast4Digits}
                onChange={handleSsnLast4Digits}
                pattern="^\d{0,4}$"
                centeredText
              />
              <SSSNTooltip
                dataAt="ssn-digits-tooltip"
                width={200}
                position={TooltipPosition.TopRight}
                toggler={
                  <i className="material-icons" data-at="ssn-digits-tooltip-toggle">
                    help_outline
                  </i>
                }
              >
                To protect your privacy and the security of your account, please enter the last 4 digits of your Social Security Number.
              </SSSNTooltip>
            </SInputWrap>
            <CommonSection>
              <SSubmitButton dataAt="continue" disabled={!isSubmitEnabled()} onClick={handleSubmit}>
                Continue
              </SSubmitButton>
            </CommonSection>
          </SInputSection>
        </SLayoutColumn>
      </SLayoutPartition>
    </SIdentification>
  );
};

export default ExistingApplication;
